import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import {ShortestRunner} from 'components/customer/Product/Drawer/helpers';

export interface CabinetDefaults {
    type: string;
    runner: string;
}

interface DrawerDefaultsType {
    defaults: CabinetDefaults;
    remember: (values: Partial<CabinetDefaults>) => void;
    chooseType: (value: string) => void;
    runnerReset: number;
    resetRunners: () => void;
    // from the runners the cabinet's type offers, for how low a face can go
    shortestRunner: ShortestRunner;
    offerShortestRunner: (runner: ShortestRunner) => void;
}

const NONE: CabinetDefaults = {type: '', runner: ''};

const DrawerDefaultsContext = createContext<DrawerDefaultsType>({
    defaults: NONE,
    remember: () => undefined,
    chooseType: () => undefined,
    runnerReset: 0,
    resetRunners: () => undefined,
    shortestRunner: null,
    offerShortestRunner: () => undefined,
});

export const useDrawerDefaults = (): DrawerDefaultsType =>
    useContext(DrawerDefaultsContext);

// The last value every drawer agreed on, kept once they diverge: override
// enough drawers and the majority becomes the override, not the default.
export const DrawerDefaultsProvider = ({
    children,
}: React.PropsWithChildren<object>) => {
    const [defaults, setDefaults] = useState<CabinetDefaults>(NONE);

    const remember = useCallback((values: Partial<CabinetDefaults>) => {
        setDefaults((current) => {
            const next = {...current, ...values};

            return next.type === current.type && next.runner === current.runner
                ? current
                : next;
        });
    }, []);

    const chooseType = useCallback((value: string) => {
        // each drawer re-picks a runner for its own face, so the old one
        // no longer describes them
        setDefaults({type: value, runner: ''});
    }, []);

    const [runnerReset, setRunnerReset] = useState(0);
    const resetRunners = useCallback(
        () => setRunnerReset((count) => count + 1),
        []
    );

    const [shortestRunner, setShortestRunner] = useState<ShortestRunner>(null);
    const offerShortestRunner = useCallback((runner: ShortestRunner) => {
        setShortestRunner((current) =>
            current?.type === runner.type && current?.height === runner.height
                ? current
                : runner
        );
    }, []);

    const value = useMemo(
        () => ({
            defaults,
            remember,
            chooseType,
            runnerReset,
            resetRunners,
            shortestRunner,
            offerShortestRunner,
        }),
        [
            defaults,
            remember,
            chooseType,
            runnerReset,
            resetRunners,
            shortestRunner,
            offerShortestRunner,
        ]
    );

    return (
        <DrawerDefaultsContext.Provider value={value}>
            {children}
        </DrawerDefaultsContext.Provider>
    );
};
